import { useCallback, useEffect, useState } from 'react'
import { api } from '../api.js'

/**
 * Busca dados da API com estados de carregamento/erro e recarga manual.
 *
 * `carregando` significa "ainda não carregou nenhuma vez", não "há uma requisição
 * em curso". A diferença importa: as telas trocam o conteúdo por "Carregando…"
 * quando `carregando` é verdadeiro, e recarregar depois de uma ação **desmontava**
 * o formulário que disparou a ação — o link de convite aparecia e sumia no mesmo
 * instante, destruído pela recarga que ele próprio pediu. Quem precisar mostrar a
 * atualização em curso usa `recarregando`, que não apaga o que está na tela.
 */
export function useDados(caminho, dependencias = []) {
  const [dados, setDados] = useState(null)
  const [erro, setErro] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [recarregando, setRecarregando] = useState(false)

  const recarregar = useCallback(async () => {
    if (!caminho) return
    setRecarregando(true)
    try {
      setDados(await api.get(caminho))
      setErro(null)
    } catch (e) {
      setErro(e)
    } finally {
      setCarregando(false)
      setRecarregando(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caminho, ...dependencias])

  useEffect(() => { recarregar() }, [recarregar])

  return { dados, erro, carregando, recarregando, recarregar, setDados }
}

export function Campo({ rotulo, dica, children }) {
  return (
    <div className="campo">
      <label>{rotulo}</label>
      {children}
      {dica && <span className="dica">{dica}</span>}
    </div>
  )
}

const ICONES = { erro: '✕', ok: '✓', atencao: '!' }

/** Status sempre com ícone + texto: nunca só cor. */
export function Aviso({ tipo = 'atencao', children }) {
  if (!children) return null
  return (
    <div className={`aviso aviso-${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      <span className="icone" aria-hidden="true">{ICONES[tipo]}</span>
      <span>{children}</span>
    </div>
  )
}

export const Carregando = ({ children = 'Carregando…' }) => (
  <p className="carregando">{children}</p>
)

export function EstadoVazio({ titulo, children }) {
  return (
    <div className="vazio">
      <p><strong>{titulo}</strong></p>
      {children}
    </div>
  )
}

/** Erro de API pronto para exibir, tratando o caso "sem conexão". */
export const Erro = ({ erro }) => (
  erro ? <Aviso tipo="erro">{erro.message}</Aviso> : null
)

// ---------------------------------------------------------------- formatação

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

export const emReais = (valor) => (valor === null || valor === undefined ? '—' : moeda.format(valor))
export const emNumero = (valor, casas = 2) => (
  valor === null || valor === undefined
    ? '—'
    : new Intl.NumberFormat('pt-BR', { maximumFractionDigits: casas }).format(valor)
)
export const emKm = (valor) => (valor === null || valor === undefined ? '—' : `${numero.format(valor)} km`)

export const emData = (iso) => (
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'
)

export const emDataHora = (iso) => (
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'
)

/**
 * Instante ISO (UTC) → valor de um <input type="datetime-local">, que trabalha em
 * hora local. Cortar a string ISO direto (`.slice(0, 16)`) põe a hora UTC num
 * campo que a lê como local: o campo mostra a hora errada e, ao salvar, o
 * lançamento anda o tamanho do fuso — três horas, no Brasil.
 */
export function paraCampoDataHora(iso) {
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** 'YYYY-MM-DD' → dd/mm/aaaa, sem passar por Date (evita deslocar o fuso). */
export const emDataSimples = (data) => (data ? data.split('-').reverse().join('/') : '—')

export const NOME_ENERGIA = {
  eletricidade: 'Eletricidade',
  gasolina: 'Gasolina',
  etanol: 'Etanol',
  diesel: 'Diesel',
  gnv: 'GNV',
}

export const NOME_PROPULSAO = {
  eletrico: 'Elétrico',
  combustao: 'Combustão',
  flex: 'Flex',
  phev: 'Híbrido plug-in',
}

export const unidadeDaEnergia = (energia) => (energia === 'eletricidade' ? 'kWh' : 'L')

/**
 * Compara textos ignorando espaçamento — o mesmo critério do servidor.
 * Confirmação de exclusão que compara o texto digitado (sem espaços) com um nome
 * gravado com espaço nas pontas nunca fecha: não existe texto que habilite o botão.
 */
export const mesmoTexto = (a, b) => (
  String(a ?? '').trim().replace(/\s+/g, ' ') === String(b ?? '').trim().replace(/\s+/g, ' ')
)

/**
 * A recarga é lançada pela leitura do relógio do ponto (SPEC §5.8)? Decide só qual
 * campo o formulário mostra — quem calcula os kWh e valida a leitura é o servidor.
 */
export const sobOMedidor = (ponto, { energia, abertura, inicio }) => Boolean(
  ponto?.medidor && energia === 'eletricidade' && !abertura && inicio >= ponto.medidor.desde,
)

/** Leitura do relógio com as casas que o próprio relógio mostra. */
export const emKwh = (valor) => (valor === null || valor === undefined ? '—' : `${emNumero(valor, 3)} kWh`)

/**
 * A bateria da recarga numa linha: a carga anotada e quanto o percurso até aqui
 * gastou (calculado no servidor). Null quando não há nada anotado.
 */
export function resumoBateria(a) {
  const partes = []
  if (a.soc_inicial !== null || a.soc_final !== null) {
    partes.push(`bateria ${a.soc_inicial ?? '?'}% → ${a.soc_final ?? '?'}%`)
  }
  const consumido = a.trecho?.soc_consumido
  if (consumido !== null && consumido !== undefined) {
    partes.push(`consumiu ${consumido}% desde a recarga anterior`)
  }
  return partes.length > 0 ? partes.join(' · ') : null
}

/** Por que este lançamento não tem consumo — o traço na tela vira explicação. */
export const MOTIVO_SEM_CONSUMO = {
  primeiro_lancamento: 'primeiro lançamento: falta o marco anterior para medir o trecho',
  parcial: 'abastecimento parcial: o consumo fecha no próximo tanque completo',
}
